import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, MapPin, ShieldCheck, Clock, Globe,
  Stethoscope, Sparkles, CheckCircle2,
} from 'lucide-react';
import { URGENT_CARE_CITIES } from '../data/urgentCareCities';
import { URGENT_PROMISE } from '../data/urgentCareContent';
import { buildLocalizedPath } from './LocaleRouter';
import { useLanguage } from '../context/LanguageContext';

/**
 * Homepage section — "Urgent Medical Care Across Vietnam".
 *
 *   • Featured live city renders as a wide hero card with trust indicators.
 *   • Coming-soon cities render as muted thumbnails — horizontal snap-scroll
 *     carousel on mobile, 4–5 col grid on desktop. Each links to a stub
 *     "Coming Soon" page (real 200, indexable, has a notify-me form).
 *
 * Driven entirely by `src/data/urgentCareCities.js` — flipping a city's
 * `status: 'live'` is the only edit required to promote it from carousel
 * thumbnail to featured hero card.
 */
const UrgentCareCitiesSection = () => {
  const { language } = useLanguage();

  const liveCities = URGENT_CARE_CITIES.filter((c) => c.status === 'live');
  const comingCities = URGENT_CARE_CITIES.filter((c) => c.status !== 'live');
  const featured = liveCities[0] || null;

  return (
    <section
      className="py-16 lg:py-24 bg-[#F8FAFC]"
      data-testid="urgent-care-cities-section"
    >
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Section header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 lg:mb-12">
          <div className="max-w-2xl">
            <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3 flex items-center gap-2">
              <Stethoscope className="h-3.5 w-3.5" />
              Urgent Medical Care
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.08]">
              24/7 Urgent Doctor Call-Outs in Vietnam
            </h2>
            <p className="text-slate-500 mt-4 text-base lg:text-lg leading-relaxed">
              {URGENT_PROMISE} We cover Ho Chi Minh City, Hanoi, Da Nang, Hoi An, Nha Trang,
              Phu Quoc and Sapa (Sa Pa), for hotel doctor visits, IV therapy, wound dressing and
              English-speaking online consultations.
            </p>
          </div>

          {featured && (
            <Link
              to={buildLocalizedPath(featured.href, language)}
              className="hidden lg:inline-flex flex-shrink-0 items-center gap-2 text-[#1FA7A1] font-semibold hover:gap-3 transition-all text-sm"
              data-testid="urgent-care-cities-see-live"
            >
              Explore city support <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>

        {/* Featured live city */}
        {featured && <FeaturedCityCard city={featured} language={language} />}

        {/* Coming soon row */}
        {comingCities.length > 0 && (
          <div className="mt-12 lg:mt-16">
            <div className="flex items-end justify-between mb-5 lg:mb-6">
              <h3 className="text-lg lg:text-xl font-extrabold text-slate-900 tracking-tight">
                Urgent care in more cities
              </h3>
              <span className="hidden sm:inline-flex items-center gap-1 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                <Stethoscope className="h-3 w-3" />
                Select a city to enquire
              </span>
            </div>

            {/* Horizontal snap-scroll on mobile, grid on desktop */}
            <div className="flex gap-4 lg:gap-5 overflow-x-auto snap-x snap-mandatory pb-3 -mx-4 px-4 lg:grid lg:grid-cols-5 lg:overflow-visible lg:mx-0 lg:px-0 lg:pb-0 no-scrollbar">
              {comingCities.map((city) => (
                <ComingSoonCard key={city.slug} city={city} language={language} />
              ))}
            </div>
          </div>
        )}

        {/* Quick links to pillar + services + safety wording (Phase 3.1) */}
        <div
          className="mt-12 bg-white rounded-3xl border border-slate-100 p-6 lg:p-8"
          style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.04)' }}
          data-testid="urgent-care-home-quicklinks"
        >
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8 items-start">
            <div className="lg:col-span-1">
              <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">
                Explore urgent care
              </p>
              <h3 className="text-xl lg:text-2xl font-extrabold text-slate-900 leading-tight">
                Need urgent care while in Vietnam?
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed mt-3">
                VietClinics provides 24/7 urgent, non-emergency doctor call-outs — a licensed
                doctor arrives within 60 minutes. We are not an ambulance service, hospital or
                emergency hotline. For life-threatening emergencies, dial <strong>115</strong> in
                Vietnam.
              </p>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-3">
              <Link
                to={buildLocalizedPath('/urgent-medical-care', language)}
                className="group flex items-center justify-between gap-3 bg-[#F0FAF8] hover:bg-[#DCEFEC] border border-[#DCEFEC] rounded-2xl px-5 py-4 transition-colors"
                data-testid="home-urgent-link-pillar"
              >
                <div>
                  <p className="font-extrabold text-slate-900 text-sm leading-tight">
                    Urgent Medical Care in Vietnam
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">Pillar hub &mdash; all services &amp; cities</p>
                </div>
                <ArrowRight className="h-4 w-4 text-[#1FA7A1] flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link
                to={buildLocalizedPath('/doctor-to-hotel-vietnam', language)}
                className="group flex items-center justify-between gap-3 bg-[#F0FAF8] hover:bg-[#DCEFEC] border border-[#DCEFEC] rounded-2xl px-5 py-4 transition-colors"
                data-testid="home-urgent-link-doctor-to-hotel"
              >
                <div>
                  <p className="font-extrabold text-slate-900 text-sm leading-tight">
                    Doctor-to-Hotel in Vietnam
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">In-room visits &amp; IV therapy</p>
                </div>
                <ArrowRight className="h-4 w-4 text-[#1FA7A1] flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link
                to={buildLocalizedPath('/english-speaking-doctor-vietnam', language)}
                className="group flex items-center justify-between gap-3 bg-[#F0FAF8] hover:bg-[#DCEFEC] border border-[#DCEFEC] rounded-2xl px-5 py-4 transition-colors"
                data-testid="home-urgent-link-english-speaking"
              >
                <div>
                  <p className="font-extrabold text-slate-900 text-sm leading-tight">
                    English-Speaking Doctors
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">Hotel, clinic &amp; online consultations</p>
                </div>
                <ArrowRight className="h-4 w-4 text-[#1FA7A1] flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link
                to={buildLocalizedPath('/urgent-medical-care-hoi-an', language)}
                className="group flex items-center justify-between gap-3 bg-[#F0FAF8] hover:bg-[#DCEFEC] border border-[#DCEFEC] rounded-2xl px-5 py-4 transition-colors"
                data-testid="home-urgent-link-hoian"
              >
                <div>
                  <p className="font-extrabold text-slate-900 text-sm leading-tight">
                    Da Nang &amp; Hoi An
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">24/7 doctor call-outs within 60 min</p>
                </div>
                <ArrowRight className="h-4 w-4 text-[#1FA7A1] flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </div>
        </div>

        {/* Foot helper */}
        <p className="text-center text-xs text-slate-500 mt-10 max-w-2xl mx-auto leading-relaxed">
          Not seeing your city? Message us — we arrange doctor call-outs in new
          areas on request.
        </p>
      </div>
    </section>
  );
};

/* ── Featured (live) city card ───────────────────────────────────────────── */

const FeaturedCityCard = ({ city, language }) => (
  <Link
    to={buildLocalizedPath(city.href, language)}
    className="group block bg-white rounded-[28px] overflow-hidden border border-slate-100 transition-all duration-500 hover:-translate-y-1"
    style={{ boxShadow: '0 30px 80px rgba(15,42,85,0.10)' }}
    data-testid={`urgent-care-city-${city.slug}`}
    data-status="enquiry"
  >
    <div className="grid grid-cols-1 lg:grid-cols-12">
      {/* Image — left half */}
      <div className="relative lg:col-span-6 aspect-[4/3] lg:aspect-auto lg:min-h-[420px] overflow-hidden bg-slate-100">
        <img
          src={city.image}
          alt={`Urgent medical care in ${city.name}`}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
          onError={(e) => { e.target.src = city.fallback; }}
        />
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(135deg, rgba(31,167,161,0.10) 0%, transparent 35%, rgba(15,42,85,0.30) 100%)' }}
        />

        {/* No real-time provider inventory is available for this card. */}
        <span
          className="absolute top-4 left-4 inline-flex items-center gap-1.5 text-[11px] font-extrabold px-3 py-1.5 rounded-full uppercase tracking-wider text-white"
          style={{ background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)', boxShadow: '0 6px 16px rgba(31,167,161,0.45)' }}
          data-testid={`urgent-care-city-${city.slug}-availability`}
        >
          <Sparkles className="h-3 w-3" />
          24/7 Support
        </span>

        {/* "Verified VietClinics Partner" pill — top-right */}
        <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1.5 rounded-full uppercase tracking-wider bg-white/95 backdrop-blur text-slate-700">
          <ShieldCheck className="h-3 w-3 text-[#1FA7A1]" />
          Coordination
        </span>

        {/* City name overlay — bottom */}
        <div className="absolute bottom-5 left-5 right-5 text-white">
          <p className="text-[11px] font-bold text-white/80 uppercase tracking-widest mb-1 flex items-center gap-1.5">
            <MapPin className="h-3 w-3" />
            {city.region}
          </p>
          <h3 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
            {city.name}
          </h3>
        </div>
      </div>

      {/* Detail — right half */}
      <div className="lg:col-span-6 p-7 lg:p-10 flex flex-col">
        <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
          24/7 doctor call-outs · {city.name}
        </p>
        {city.coverage && (
          <p
            className="inline-flex items-center gap-1.5 text-[11.5px] text-slate-500 mb-3 -mt-1"
            data-testid={`urgent-care-city-${city.slug}-coverage`}
          >
            <MapPin className="h-3 w-3 text-slate-400" />
            A doctor arrives within 60 minutes
          </p>
        )}
        <h4 className="text-xl lg:text-2xl font-extrabold text-slate-900 tracking-tight leading-snug mb-3">
          A licensed doctor to your door in 60 minutes
        </h4>
        <p className="text-slate-600 text-sm lg:text-base leading-relaxed mb-6">
          A licensed, English-speaking doctor comes to your hotel, apartment or
          Airbnb within 60 minutes for an in-person consultation, IV therapy or
          wound dressing — 24 hours a day.
        </p>

        {/* Trust grid */}
        {Array.isArray(city.trustPoints) && city.trustPoints.length > 0 && (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-2.5 gap-x-4 mb-7">
            {['Doctor arrives within 60 minutes', 'English-speaking doctors', 'IV therapy & wound dressing', 'Insurance documents on request'].map((point) => (
              <li key={point} className="flex items-start gap-2 text-sm text-slate-700">
                <CheckCircle2 className="h-4 w-4 text-[#1FA7A1] flex-shrink-0 mt-0.5" />
                <span className="leading-snug">{point}</span>
              </li>
            ))}
          </ul>
        )}

        {/* CTA row */}
        <div className="mt-auto flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <span
            className="inline-flex items-center justify-center gap-2 text-white font-bold rounded-2xl px-7 py-3.5 transition-all duration-300 group-hover:-translate-y-0.5"
            style={{
              background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)',
              boxShadow: '0 12px 24px rgba(31,167,161,0.30)',
            }}
            data-testid={`urgent-care-city-${city.slug}-cta`}
          >
            <Stethoscope className="h-4 w-4" />
            Get Urgent Care Support
            <ArrowRight className="h-4 w-4" />
          </span>
          {city.fromPrice && (
            <span className="inline-flex items-center gap-2 text-sm font-bold text-slate-700">
              <Clock className="h-4 w-4 text-slate-400" />
              Pricing confirmed by provider
            </span>
          )}
        </div>

        {/* Micro trust footer */}
        <div className="mt-6 pt-5 border-t border-slate-100 flex flex-wrap items-center gap-x-5 gap-y-2">
          {[
            { icon: Globe, label: 'English-speaking doctors' },
            { icon: Clock, label: 'Arrives within 60 minutes' },
            { icon: ShieldCheck, label: 'Non-emergency call-outs' },
          ].map(({ icon: Icon, label }) => (
            <span key={label} className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              <Icon className="h-3 w-3 text-[#1FA7A1]" />
              {label}
            </span>
          ))}
        </div>
      </div>
    </div>
  </Link>
);

/* ── Coming-soon city card (muted) ───────────────────────────────────────── */

const ComingSoonCard = ({ city, language }) => (
  <Link
    to={buildLocalizedPath(city.href, language)}
    className="group relative flex-shrink-0 w-[260px] lg:w-auto snap-start bg-white rounded-[22px] border border-slate-100 overflow-hidden flex flex-col hover:-translate-y-0.5 transition-all duration-300"
    style={{ boxShadow: '0 6px 20px rgba(15,42,85,0.06)' }}
    data-testid={`urgent-care-city-${city.slug}`}
    data-status="enquiry"
  >
    <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
      <img
        src={city.image}
        alt={`Urgent medical care in ${city.name}`}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        onError={(e) => { e.target.src = city.fallback; }}
      />
      {/* Gradient overlay */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(180deg, rgba(15,42,85,0.10) 0%, rgba(15,42,85,0.05) 40%, rgba(15,42,85,0.55) 100%)' }}
      />

      {/* Availability badge */}
      <span
        className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] font-extrabold px-2.5 py-1 rounded-full uppercase tracking-wider text-white"
        style={{ background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)', boxShadow: '0 6px 16px rgba(31,167,161,0.45)' }}
        data-testid={`urgent-care-city-${city.slug}-availability`}
      >
        <Sparkles className="h-3 w-3" />
        24/7 Support
      </span>

      {/* Name + region */}
      <div className="absolute bottom-3 left-3 right-3 text-white">
        <h4 className="text-lg font-extrabold leading-tight">{city.name}</h4>
        <p className="text-[10px] font-bold text-white/80 uppercase tracking-widest mt-0.5 flex items-center gap-1">
          <MapPin className="h-2.5 w-2.5" />
          {city.region}
        </p>
      </div>
    </div>

    <div className="p-4 flex items-center justify-between gap-2">
      <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#1FA7A1] uppercase tracking-wider">
        <Stethoscope className="h-3.5 w-3.5" />
        Get Urgent Care Support
      </span>
      <ArrowRight className="h-3.5 w-3.5 text-slate-400 group-hover:text-[#1FA7A1] group-hover:translate-x-0.5 transition-all" />
    </div>
  </Link>
);

export default UrgentCareCitiesSection;
